import { useEffect, useMemo, useRef, useState } from "react";
import { importTimesheets, timesheetAlias } from "../utils/api";

/* ============================================================================
 * FreshopsUpload — drag/drop the daily freshOps CSV, review the match
 * summary, approve/decline suggested name pairings, then push to Airtable.
 *
 * Flow: drop file -> preview (nothing written) -> totals buttons:
 *   Total Amount | Matched | Need Approval
 * -> Need Approval rows have Approve / Decline (spinner while saving, then
 * the preview refreshes so the name moves to Matched) -> Submit pushes with
 * commit=true.
 * ==========================================================================*/

const toneColor = (t) =>
  t === "red" ? "#f87171" : t === "green" ? "#4ade80" : t === "amber" ? "#fbbf24" : "#94a3b8";

const S = {
  backdrop: { position:"fixed", inset:0, top: "var(--fr-modal-top, 0px)", background:"rgba(2,6,23,0.7)", zIndex:1100,
    display:"flex", alignItems:"flex-start", justifyContent:"center", padding:"40px 16px", overflowY:"auto" },
  modal: { position:"relative", maxHeight: "calc(var(--fr-modal-max, 82vh) - 76px)", overflowY: "auto", overscrollBehavior: "contain", width:"100%", maxWidth:900,
    background:"linear-gradient(135deg,#1e293b 0%,#0f172a 100%)",
    border:"1px solid rgba(34,211,238,0.25)", borderRadius:16, padding:"18px 18px 14px" },
  accent: { position:"absolute", top:0, left:0, right:0, height:3,
    background:"linear-gradient(90deg,#22d3ee,#3b82f6,#8b5cf6)" },
  head: { position: "sticky", top: 0, zIndex: 5, background: "#1e293b", boxShadow: "0 -18px 0 0 #1e293b",  display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14 },
  title: { margin:0, fontSize:"1rem", fontWeight:700, color:"#f1f5f9" },
  closeX: { background:"none", border:"none", color:"rgba(255,255,255,0.6)", fontSize:"1.4rem", lineHeight:1, cursor:"pointer" },
  drop: (over) => ({ border:`2px dashed ${over ? "#22d3ee" : "rgba(34,211,238,0.35)"}`,
    borderRadius:14, padding:"44px 20px", textAlign:"center", cursor:"pointer",
    background: over ? "rgba(34,211,238,0.08)" : "rgba(255,255,255,0.02)", transition:"all .15s ease" }),
  dropTitle: { color:"#e2e8f0", fontWeight:700, fontSize:"0.95rem" },
  dropHint: { color:"rgba(255,255,255,0.45)", fontSize:"0.78rem", marginTop:6 },
  gap: { margin:"10px 0", padding:"9px 12px", borderRadius:10, fontSize:"0.78rem", lineHeight:1.5,
    background:"rgba(251,191,36,0.08)", border:"1px solid rgba(251,191,36,0.3)", color:"#fbbf24" },
  buttonsGrid: { display:"grid", gridTemplateColumns:"repeat(3,minmax(0,1fr))", gap:10, margin:"12px 0 8px" },
  countBtn: (active) => ({ borderRadius:12, padding:"12px 10px", cursor:"pointer",
    background: active ? "rgba(34,211,238,0.15)" : "rgba(34,211,238,0.08)",
    border:`1px solid ${active ? "rgba(34,211,238,0.6)" : "rgba(34,211,238,0.25)"}`,
    display:"flex", flexDirection:"column", alignItems:"center", gap:4 }),
  countLabel: { fontSize:"0.68rem", color:"rgba(255,255,255,0.55)", textTransform:"uppercase",
    letterSpacing:"0.05em", fontWeight:700 },
  countValue: (tone) => ({ fontSize:"1.7rem", fontWeight:800, lineHeight:1, color: toneColor(tone) }),
  card: { background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)",
    borderRadius:12, padding:"12px", marginTop:10 },
  colHead: { fontSize:"0.62rem", letterSpacing:"0.05em", textTransform:"uppercase",
    color:"rgba(255,255,255,0.45)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  cell: { fontSize:"0.82rem", color:"#e2e8f0", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  name: { fontSize:"0.84rem", fontWeight:700, color:"#22d3ee", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  tierChip: (tier) => ({ fontSize:"0.6rem", fontWeight:700, letterSpacing:"0.04em",
    padding:"1px 7px", borderRadius:999,
    background: tier === "manual" ? "rgba(168,85,247,0.15)" : tier === "fuzzy" ? "rgba(251,191,36,0.15)"
      : tier === "notworking" || tier === "pending" ? "rgba(148,163,184,0.15)" : "rgba(74,222,128,0.12)",
    color: tier === "manual" ? "#c084fc" : tier === "fuzzy" ? "#fbbf24"
      : tier === "notworking" || tier === "pending" ? "#94a3b8" : "#4ade80",
    border: `1px solid ${tier === "manual" ? "rgba(168,85,247,0.4)" : tier === "fuzzy" ? "rgba(251,191,36,0.4)"
      : tier === "notworking" || tier === "pending" ? "rgba(148,163,184,0.4)" : "rgba(74,222,128,0.35)"}` }),
  rowBtn: (kind, busy) => ({ padding:"5px 12px", borderRadius:8, fontSize:"0.72rem", fontWeight:700,
    cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1, display:"inline-flex", alignItems:"center", gap:6,
    border:`1px solid ${kind === "ok" ? "rgba(74,222,128,0.5)" : kind === "nw" ? "rgba(148,163,184,0.5)" : "rgba(248,113,113,0.5)"}`,
    background: kind === "ok" ? "rgba(74,222,128,0.12)" : kind === "nw" ? "rgba(148,163,184,0.1)" : "rgba(248,113,113,0.1)",
    color: kind === "ok" ? "#4ade80" : kind === "nw" ? "#94a3b8" : "#f87171" }),
  submit: (busy, ready) => ({ width:"100%", padding:"12px", borderRadius:10, border:"none", marginTop:12,
    background: ready ? "linear-gradient(135deg,#22d3ee 0%,#3b82f6 100%)" : "rgba(255,255,255,0.06)",
    color: ready ? "#0f172a" : "rgba(255,255,255,0.5)", fontWeight:800, fontSize:"0.9rem",
    cursor: busy ? "wait" : "pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }),
  done: { margin:"14px 0", padding:"14px", borderRadius:12, textAlign:"center", fontSize:"0.9rem",
    background:"rgba(74,222,128,0.08)", border:"1px solid rgba(74,222,128,0.35)", color:"#4ade80", fontWeight:700 },
  hint: { marginTop:8, fontSize:"0.72rem", color:"rgba(255,255,255,0.4)", textAlign:"center" },
  err: { margin:"10px 0", padding:"9px 12px", borderRadius:10, fontSize:"0.8rem",
    background:"rgba(248,113,113,0.08)", border:"1px solid rgba(248,113,113,0.35)", color:"#fca5a5" },
};

const Spinner = () => (
  <span style={{ width:12, height:12, border:"2px solid currentColor", borderTopColor:"transparent",
    borderRadius:"50%", display:"inline-block", animation:"fu-spin 0.7s linear infinite" }} />
);

const BUCKETS = [
  { key:"total",    label:"Total Amount",  tone:"slate" },
  { key:"matched",  label:"Matched",       tone:"green" },
  { key:"approval", label:"Need Approval", tone:"amber" },
];

const FreshopsUpload = ({ onClose }) => {
  const inline = !onClose;                    // rendered inside a tab, not as a modal
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);          // preview/commit in flight
  const [preview, setPreview] = useState(null);     // summary from backend
  const [selection, setSelection] = useState(null);
  const [rowBusy, setRowBusy] = useState({});       // "name|db|action" -> true
  const [resolved, setResolved] = useState({});     // name -> "approved"|"declined"|"notworking"
  
  const [result, setResult] = useState(null);       // commit result
  const [error, setError] = useState("");
  const [confirmArm, setConfirmArm] = useState(false);   // 2nd-click guard
  const inputRef = useRef(null);

  const runPreview = async (f, keepSelection = false) => {
    setBusy(true); setError(""); setResult(null);
    const res = await importTimesheets(f, false);
    setBusy(false);
    if (res.status === "success") {
      setPreview(res);
      if (!keepSelection) setSelection(null);   // new file resets; decisions don't
    } else {
      setError(res.error || "Could not read that file");
    }
  };

  const pickFile = (f) => {
    if (!f) return;
    setFile(f);
    runPreview(f);
  };

  const decide = async (freshopsName, systemName, decision) => {
    const key = `${freshopsName}|${decision}`;
    setRowBusy((p) => ({ ...p, [key]: true }));
    const res = await timesheetAlias({ freshopsName, systemName, decision });
    if (res.status === "success") {
      setResolved((p) => ({ ...p, [freshopsName]: decision }));
      await runPreview(file, true);                 // refresh but STAY on this table
    } else {
      setError(res.error || "Could not save that decision");
    }
    setRowBusy((p) => ({ ...p, [key]: false }));
  };

  const rows = preview?.rowsPreview || [];
  const matchedRows = useMemo(() => rows.filter((r) => r["Match Tier"] !== "none" && r["Match Tier"] !== "ambiguous"), [rows]);
  const needApproval = (preview?.unmatchedNames || []).filter((u) => !resolved[u.name]);
  
  const counts = {
    total: preview?.totalRows ?? 0,
    matched: preview?.matchedRows ?? 0,
    approval: needApproval.length,
  };
  const gap = preview?.gap;

  useEffect(() => {
    if (!confirmArm) return;
    const t = setTimeout(() => setConfirmArm(false), 4000);   // disarm after 4s
    return () => clearTimeout(t);
  }, [confirmArm]);




  const commit = async () => {
    if (counts.approval > 0 && !confirmArm) {
      setConfirmArm(true);                    // first click arms; second confirms
      setSelection("approval");               // and shows what still needs review
      return;
    }
    setConfirmArm(false);
    setBusy(true); setError("");
    const res = await importTimesheets(file, true);
    setBusy(false);
    if (res.status === "success") setResult(res);
    else setError(res.error || "Push failed");
  };


  const table = (cols, body) => (
    <div style={S.card}>
      <div style={{ maxHeight:300, overflowY:"auto", overflowX:"auto" }}>
        <div style={{ minWidth:640 }}>
          <div style={{ display:"grid", gridTemplateColumns:cols, padding:"8px 8px 10px", columnGap:8,
            borderBottom:"1px solid rgba(255,255,255,0.12)", position:"sticky", top:0, background:"#1b2537", zIndex:1 }}>
            {body.head.map((h) => <span key={h} style={S.colHead}>{h}</span>)}
          </div>
          {body.rows}
        </div>
      </div>
    </div>
  );

  const shell = (
    <>
      <style>{`@keyframes fu-spin{to{transform:rotate(360deg)}}
        .fu-row{transition:background .12s ease}.fu-row:hover{background:rgba(34,211,238,0.06)}`}</style>
      <div style={{ ...S.modal, margin: inline ? "0 auto" : undefined }}
        onClick={(e) => e.stopPropagation()}>
        <div style={S.accent} />
        <div style={S.head}>
          <h4 style={S.title}>freshOps Timesheet Upload</h4>
          {!inline && (
            <button type="button" style={S.closeX} onClick={onClose} aria-label="Close">×</button>
          )}
        </div>

        {/* dropzone */}
        {!result && (
          <div
            style={S.drop(dragOver)}
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
          >
            <input ref={inputRef} type="file" accept=".csv" style={{ display:"none" }}
              onChange={(e) => pickFile(e.target.files?.[0])} />
            <div style={S.dropTitle}>
              {busy && !preview ? "Reading file…" : file ? `📄 ${file.name}` : "Drag & drop the freshOps CSV here"}
            </div>
            <div style={S.dropHint}>{file ? "Drop a different file to replace it" : "or click to browse"}</div>
          </div>
        )}

        {gap && gap.needed?.length > 0 && !result && (
          <div style={S.gap}>
            📅 Uploaded through <strong>{gap.uploadedThrough || "—"}</strong>.
            Still needed: <strong>{gap.needed.join(", ")}</strong>
          </div>
        )}
        {error && <div style={S.err}>{error}</div>}

        {/* totals */}
        {preview && !result && (
          <>
            <div style={S.buttonsGrid}>
              {BUCKETS.map((b) => (
                <button key={b.key} type="button" style={S.countBtn(selection === b.key)}
                  onClick={() => setSelection((p) => (p === b.key ? null : b.key))}>
                  <span style={S.countLabel}>{b.label}</span>
                  <span style={S.countValue(b.tone)}>{busy ? "…" : counts[b.key]}</span>
                </button>
              ))}
            </div>

            {selection === "total" && table("1.4fr 1.2fr 0.7fr 1fr 1fr 0.7fr", {
              head: ["Name", "System Name", "Tier", "Date", "Site", "Hours"],
              rows: rows.map((r, i) => (
                <div key={i} className="fu-row" style={{ display:"grid", gridTemplateColumns:"1.4fr 1.2fr 0.7fr 1fr 1fr 0.7fr",
                  alignItems:"center", padding:"9px 8px", columnGap:8,
                  borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                  <span style={S.name}>{r["Employee Name"]}</span>
                  <span style={S.cell}>{r["System Name"] || "—"}</span>
                  <span><span style={S.tierChip(r["Match Tier"])}>{r["Match Tier"]}</span></span>
                  <span style={S.cell}>{r["Date"]}</span>
                  <span style={S.cell}>{r["Site Name"]}</span>
                  <span style={S.cell}>{r["Adjusted Hours"]}</span>
                </div>
              )),
            })}

            {selection === "matched" && table("1.4fr 1.2fr 0.7fr 1fr 1fr 0.7fr", {
              head: ["Name", "System Name", "Tier", "Date", "Site", "Hours"],
              rows: matchedRows.map((r, i) => (
                <div key={i} className="fu-row" style={{ display:"grid", gridTemplateColumns:"1.4fr 1.2fr 0.7fr 1fr 1fr 0.7fr",
                  alignItems:"center", padding:"9px 8px", columnGap:8,
                  borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                  <span style={S.name}>{r["Employee Name"]}</span>
                  <span style={S.cell}>{r["System Name"]}</span>
                  <span><span style={S.tierChip(r["Match Tier"])}>{r["Match Tier"]}</span></span>
                  <span style={S.cell}>{r["Date"]}</span>
                  <span style={S.cell}>{r["Site Name"]}</span>
                  <span style={S.cell}>{r["Adjusted Hours"]}</span>
                </div>
              )),
            })}

            {selection === "approval" && (
              <div style={S.card}>
                {needApproval.length === 0 ? (
                  <div style={{ ...S.hint, padding:"14px 0" }}>Nothing awaiting approval 🎉</div>
                ) : needApproval.map((u, i) => (
                  <div key={u.name} style={{ padding:"10px 6px",
                    borderTop: i ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                      <div>
                        <div style={S.name}>{u.name}
                          <span style={{ marginLeft:8, fontSize:"0.68rem", color:"rgba(255,255,255,0.45)" }}>
                            {u.shifts} shift{u.shifts === 1 ? "" : "s"}
                          </span>
                        </div>
                        {u.suggestions?.length > 0 ? (
                          <div style={{ fontSize:"0.8rem", color:"#e2e8f0", marginTop:3 }}>
                            Suggested: <strong style={{ color:"#fbbf24" }}>{u.suggestions[0].db}</strong>
                            <span style={{ color:"rgba(255,255,255,0.4)", marginLeft:6 }}>
                              (score {u.suggestions[0].score})
                            </span>
                          </div>
                        ) : (
                          <div style={{ fontSize:"0.78rem", color:"rgba(255,255,255,0.45)", marginTop:3 }}>
                            No match found in the database — imports unlinked unless added to the system first.
                          </div>
                        )}
                      </div>
                      <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                        {u.suggestions?.length > 0 && (
                          <>
                            <button type="button"
                              style={S.rowBtn("ok", rowBusy[`${u.name}|approved`])}
                              disabled={rowBusy[`${u.name}|approved`] || rowBusy[`${u.name}|declined`]}
                              onClick={() => decide(u.name, u.suggestions[0].db, "approved")}>
                              {rowBusy[`${u.name}|approved`] ? <Spinner /> : "✓"} Approve
                            </button>
                            <button type="button"
                              style={S.rowBtn("no", rowBusy[`${u.name}|declined`])}
                              disabled={rowBusy[`${u.name}|approved`] || rowBusy[`${u.name}|declined`]}
                              onClick={() => decide(u.name, u.suggestions[0].db, "declined")}>
                              {rowBusy[`${u.name}|declined`] ? <Spinner /> : "✗"} Decline
                            </button>
                          </>
                        )}
                        <button type="button"
                          style={S.rowBtn("nw", rowBusy[`${u.name}|pending`])}
                          disabled={rowBusy[`${u.name}|approved`] || rowBusy[`${u.name}|declined`] || rowBusy[`${u.name}|pending`]}
                          title="Not a system user — imports under their freshOps name and won't prompt again"
                          onClick={() => decide(u.name, u.name, "pending")}>
                          {rowBusy[`${u.name}|pending`] ? <Spinner /> : "⏻"} Not Working
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button type="button"
              style={{ ...S.submit(busy, counts.approval === 0),
                ...(confirmArm ? { background:"rgba(251,191,36,0.2)", color:"#fbbf24",
                  border:"1px solid rgba(251,191,36,0.6)" } : {}) }}
              onClick={commit} disabled={busy}>
              {busy ? <Spinner /> : confirmArm ? "⚠" : "⬆"}{" "}
              {confirmArm
                ? `${counts.approval} name${counts.approval === 1 ? "" : "s"} unreviewed — click again to push anyway`
                : `Push ${counts.total} rows to Timesheets${counts.approval > 0
                    ? ` (${counts.approval} name${counts.approval === 1 ? "" : "s"} still unlinked)` : ""}`}
            </button>
            <div style={S.hint}>
              Nothing is written until you push. Approving a pairing saves it permanently — that name
              matches automatically on every future upload.
            </div>
          </>
        )}

        {result && (
          <div style={S.done}>
            ✅ Pushed — {result.created} created, {result.updated} updated
            {result.failed ? `, ${result.failed} failed` : ""} for {result.dates?.join(", ")}
            <div style={{ marginTop:10 }}>
              <button type="button" style={{ ...S.rowBtn("ok", false), fontSize:"0.8rem" }}
                onClick={() => { setFile(null); setPreview(null); setResult(null); setSelection(null); }}>
                Upload another file
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
  return inline ? shell : (
    <div style={S.backdrop} onClick={onClose}>{shell}</div>
  );
};

export default FreshopsUpload;